import { useState, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import PpLogo from './PpLogo';
import ShopifyAppUpdateDialog from './ShopifyAppUpdateDialog';
import AdyenPaymentImportDialog from './AdyenPaymentImportDialog';
import AdyenDisputeImportDialog from './AdyenDisputeImportDialog';
import AdyenPaymentDaysDialog from './AdyenPaymentDaysDialog';
import { AdyenPaymentDayCount } from '../../types/adyenPaymentDays';
import {
  PPAccountIntegration,
  Brand,
  AutoRefundMode,
  AutoRefundModeLabel,
  getAutoRefundMode,
  CancellationMode,
  CancellationModeLabel,
  getLoopCancellationMode,
  getLoopCancellationOnChargebackMode,
  getSubscriptionCancelMode,
  ExternalRefundMode,
  ExternalRefundModeLabel,
  getExternalRefundMode,
  MarkChargebackMode,
  MarkChargebackModeLabel,
  PpAccountSettingsPayload,
  CredentialProbeResult,
} from '../../types/paymentProcessor';
import { getMarkChargebackMode } from '../../utils/markChargebackMode';
import { getKachingCancellationMode, getKachingCancellationOnChargebackMode } from '../../utils/subscriptionAppModes';
import { formatCurrency } from '../../utils/formatCurrency';
import LightTooltip from './LightTooltip';
import { CRM_CONNECTOR_CAPABILITIES, CRM_CONNECTOR_DRIVERS, CRM_CONNECTOR_PROXY_IP } from '../../constants/crmConnector';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import { HasPermission } from '../../functions/auth';
import { hasPermission } from '../../functions/permissionUtils';
import { useStateValue } from '../../context/StateValueContext';
import { PERMISSIONS } from '../../constants/permissions';
import SubscriptionAppTokenDialog, { type SubscriptionApp } from './SubscriptionAppTokenDialog';
import CredentialDialog from './CredentialDialog';
import CrmConnectorSandboxDialog from './CrmConnectorSandboxDialog';
import CrmConnectorCacheWindowDialog from './CrmConnectorCacheWindowDialog';
import { CRM_MAX_CACHE_WINDOW_DAYS } from '../../constants/crmConnector';
import CrmConnectorActionsDialog from './CrmConnectorActionsDialog';
import RefundMaxAmountDialog from './RefundMaxAmountDialog';
import RepresentmentStartDateDialog from './RepresentmentStartDateDialog';
import CheckoutChampCredentialCheckDialog from './CheckoutChampCredentialCheckDialog';
import CheckoutChampPasswordDialog from './CheckoutChampPasswordDialog';

interface PaymentProcessorsTableProps {
  accounts: PPAccountIntegration[];
  loading: boolean;
  downloadingAccountId?: number | null;
  handleStripeDownloadDisputes?: (accountId: number) => void;
  handleShopifyDownloadDisputes?: (accountId: number) => void;
  handlePayPalDownloadDisputes?: (accountId: number) => void;
  brands?: Brand[];
  companyRefundMaxAmount?: number | null;
  onBrandChange?: (ppType: string, accountId: number, brandId: number) => Promise<void>;
  onAdyenMapMerchants?: (accountId: number, accountName: string) => void;
  onAccountSettingsSave?: (
    ppType: string,
    accountId: number,
    payload: PpAccountSettingsPayload,
  ) => Promise<void>;
  onRefundMaxSave?: (ppType: string, accountId: number, cents: number | null) => Promise<void>;
  onRepresentmentStartDateSave?: (ppType: string, accountId: number, date: string) => Promise<void>;
  onShopifyAppInstall?: () => void;
  onAdyenImportDisputes?: (accountId: number, file: File) => Promise<void>;
  onAdyenImportPayments?: (accountId: number, file: File) => Promise<void>;
  onAdyenPaymentDays?: (accountId: number) => Promise<AdyenPaymentDayCount[]>;
  onVerifyCheckoutChampCredentials?: (accountId: number) => Promise<CredentialProbeResult[]>;
  onChangeCheckoutChampPassword?: (accountId: number, password: string) => Promise<void>;
}

const AUTO_REFUND_MODES: AutoRefundMode[] = ['off', 'read_only', 'refund'];
const CANCELLATION_MODES: CancellationMode[] = ['off', 'read_only', 'cancel'];
const EXTERNAL_REFUND_MODES: ExternalRefundMode[] = ['off', 'read_only', 'refund'];
const MARK_CHARGEBACK_MODES: MarkChargebackMode[] = ['off', 'read_only', 'mark'];

// The providers VAMP rates, mirroring VAMP_PROVIDERS in the API's vampRatesRepository. PayPal is
// absent there, so a toggle for it would gate nothing.
const VAMP_RATED_PP_TYPES = ['shopify', 'stripe', 'checkout_champ', 'adyen'];

const STRIPE_API_KEY_HELP =
  'The new key must belong to the same Stripe account and be able to read disputes.';

const CRM_CONNECTOR_API_KEY_HELP =
  'Validated against GET /gateways before it is saved. Re-enable the account afterwards if the CRM had disabled it.';

const chipColorForAutoRefund = (m: AutoRefundMode) =>
  m === 'off' ? 'default' : m === 'read_only' ? 'warning' : 'success';

const chipColorForCancellation = (m: CancellationMode) =>
  m === 'off' ? 'default' : m === 'read_only' ? 'warning' : 'success';

const chipColorForExternalRefund = (m: ExternalRefundMode) =>
  m === 'off' ? 'default' : m === 'read_only' ? 'warning' : 'success';

const chipColorForMarkChargeback = (m: MarkChargebackMode) =>
  m === 'off' ? 'default' : m === 'read_only' ? 'warning' : 'success';

type PendingModeChange = { title: string; description: ReactNode; onConfirm: () => Promise<void> };
type ShopifyUpdateAccount = { accountName: string };
type CacheTarget = { ppType: string; accountId: number; accountName: string; wasEnabled: boolean };
type CustomerInfoConfirm = { ppType: string; accountId: number; accountName: string; enabled: boolean; cacheOff: boolean };
type DisableTarget = { ppType: string; accountId: number; accountName: string; isCurrentlyDisabled: boolean };
type AdyenImportTarget = { accountId: number; accountName: string };

function MonoValue({ value }: { value: string | null | undefined }) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontFamily: 'monospace',
        color: value ? 'text.primary' : 'text.secondary',
        wordBreak: 'break-all',
      }}
    >
      {value || '—'}
    </Typography>
  );
}

function EditableValue({ children, onEdit }: { children: ReactNode; onEdit?: () => void }) {
  if (!onEdit) return <>{children}</>;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        '& .editable-value-icon': { opacity: 0.4 },
        '&:hover .editable-value-icon': { opacity: 1 },
      }}
      onClick={onEdit}
    >
      {children}
      <EditIcon className="editable-value-icon" sx={{ fontSize: 14, color: 'text.secondary' }} />
    </Box>
  );
}

function EditableMonoValue({ value, onEdit }: { value: string | null | undefined; onEdit?: () => void }) {
  return (
    <EditableValue onEdit={onEdit}>
      <MonoValue value={value} />
    </EditableValue>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1, alignItems: 'center' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function DisputesApiChip({ on, offTitle }: { on: boolean; offTitle: string }) {
  return (
    <Box data-testid="disputes-api" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Chip label={on ? 'On' : 'Off'} size="small" color={on ? 'success' : 'default'} variant="outlined" sx={{ fontSize: '0.75rem' }} />
      {!on && (
        <LightTooltip title={offTitle} placement="top">
          <ErrorOutlineIcon color="warning" fontSize="small" sx={{ cursor: 'help' }} />
        </LightTooltip>
      )}
    </Box>
  );
}

function ModeSelectRow<M extends string>({
  label,
  mode,
  modes,
  modeLabel,
  chipColor,
  onPick,
  disabledItem,
  adornment,
}: {
  label: string;
  mode: M;
  modes: M[];
  modeLabel: Record<M, string>;
  chipColor: (m: M) => 'default' | 'warning' | 'success';
  onPick: (m: M) => void;
  disabledItem?: (m: M) => { disabled: boolean; reason?: string };
  adornment?: ReactNode;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <InfoRow label={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Chip
          label={modeLabel[mode]}
          size="small"
          color={chipColor(mode)}
          variant="outlined"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
        />
        {adornment}
        <Menu
          anchorEl={anchor}
          open={!!anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {modes.map((m) => {
            const itemState = disabledItem?.(m);
            return (
              <MenuItem
                key={m}
                selected={m === mode}
                disabled={itemState?.disabled}
                onClick={() => {
                  setAnchor(null);
                  if (m !== mode) onPick(m);
                }}
              >
                {modeLabel[m]}
                {itemState?.disabled && itemState.reason ? ` — ${itemState.reason}` : ''}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    </InfoRow>
  );
}

function formatVersion(value: string | null | undefined) {
  const version = value || 'v14';
  return version.startsWith('v') ? version : `v${version}`;
}

export default function PaymentProcessorsTable({
  accounts,
  loading,
  downloadingAccountId,
  handleStripeDownloadDisputes,
  handleShopifyDownloadDisputes,
  handlePayPalDownloadDisputes,
  brands = [],
  onAdyenMapMerchants,
  companyRefundMaxAmount,
  onBrandChange,
  onAccountSettingsSave,
  onRefundMaxSave,
  onRepresentmentStartDateSave,
  onShopifyAppInstall,
  onAdyenImportDisputes,
  onAdyenImportPayments,
  onAdyenPaymentDays,
  onVerifyCheckoutChampCredentials,
  onChangeCheckoutChampPassword,
}: PaymentProcessorsTableProps) {
  const [brandMenuAnchor, setBrandMenuAnchor] = useState<HTMLElement | null>(null);
  const [editingBrandKey, setEditingBrandKey] = useState<string | null>(null);

  const [pendingModeChange, setPendingModeChange] = useState<PendingModeChange | null>(null);
  const [pendingModeError, setPendingModeError] = useState<string | null>(null);
  const [pendingModeSaving, setPendingModeSaving] = useState(false);

  const [tokenDialog, setTokenDialog] = useState<{ account: PPAccountIntegration; app: SubscriptionApp } | null>(null);
  const [apiKeyAccount, setApiKeyAccount] = useState<PPAccountIntegration | null>(null);
  const [connectorKeyAccount, setConnectorKeyAccount] = useState<PPAccountIntegration | null>(null);
  const [actionsAccount, setActionsAccount] = useState<PPAccountIntegration | null>(null);
  const [sandboxAccount, setSandboxAccount] = useState<PPAccountIntegration | null>(null);
  const [cacheWindowAccount, setCacheWindowAccount] = useState<PPAccountIntegration | null>(null);
  const [shopifyUpdateAccount, setShopifyUpdateAccount] = useState<ShopifyUpdateAccount | null>(null);

  const [cacheTarget, setCacheTarget] = useState<CacheTarget | null>(null);
  const [cacheEnabledInput, setCacheEnabledInput] = useState(false);

  const [representmentTarget, setRepresentmentTarget] = useState<{
    ppType: string; accountId: number; accountName: string; representmentStartDate?: string;
  } | null>(null);
  const [refundMaxTarget, setRefundMaxTarget] = useState<{
    ppType: string; accountId: number; accountName: string; refundMaxAmount: number | null | undefined;
  } | null>(null);

  const [customerInfoMenuAnchor, setCustomerInfoMenuAnchor] = useState<HTMLElement | null>(null);
  const [editingCustomerInfoKey, setEditingCustomerInfoKey] = useState<string | null>(null);
  const [customerInfoConfirm, setCustomerInfoConfirm] = useState<CustomerInfoConfirm | null>(null);

  const [disableTarget, setDisableTarget] = useState<DisableTarget | null>(null);
  const [adyenImportTarget, setAdyenImportTarget] = useState<AdyenImportTarget | null>(null);
  const [adyenPaymentImportTarget, setAdyenPaymentImportTarget] = useState<AdyenImportTarget | null>(null);
  const [adyenPaymentDaysTarget, setAdyenPaymentDaysTarget] = useState<AdyenImportTarget | null>(null);
  const [disableReasonInput, setDisableReasonInput] = useState('');
  const [credentialCheckAccount, setCredentialCheckAccount] = useState<{ accountId: number; accountName: string } | null>(null);
  const [passwordChangeAccount, setPasswordChangeAccount] = useState<{ accountId: number; accountName: string; loginId?: string } | null>(null);

  const [{ user }] = useStateValue();
  const canWriteCompanies = hasPermission(user, PERMISSIONS.COMPANIES_WRITE_ALL);

  const canEditAccountSettings = canWriteCompanies && !!onAccountSettingsSave;

  const openDisableDialog = (account: PPAccountIntegration) => {
    setDisableReasonInput('');
    setDisableTarget({
      ppType: account.ppType,
      accountId: account.ppAccountId,
      accountName: account.name,
      isCurrentlyDisabled: account.enabled === false,
    });
  };

  const renderSyncAction = (account: PPAccountIntegration) => {
    const handler =
      account.ppType === 'stripe' ? handleStripeDownloadDisputes
      : account.ppType === 'shopify' ? handleShopifyDownloadDisputes
      : account.ppType === 'paypal' ? handlePayPalDownloadDisputes
      : null;
    if (!handler) return null;
    const labelMap: Record<string, string> = {
      stripe: 'Sync Stripe Disputes (90 days)',
      shopify: 'Sync Shopify Disputes (90 days)',
      paypal: 'Sync PayPal Disputes (90 days)',
    };
    return (
      <LightTooltip title={labelMap[account.ppType]} placement="top-end">
        <IconButton
          onClick={() => handler(account.ppAccountId)}
          disabled={!!downloadingAccountId}
          color="primary"
          size="small"
        >
          <SyncIcon />
        </IconButton>
      </LightTooltip>
    );
  };

  const renderEnabledToggle = (account: PPAccountIntegration) => {
    if (!canEditAccountSettings) return null;
    const isEnabled = account.enabled !== false;
    const tooltip = isEnabled ? (
      'Account enabled — turn off to disable'
    ) : (
      <>
        {account.disabledReason || 'No reason provided'}
        {account.disabledByName && (
          <Box sx={{ mt: 0.75 }}>Disabled by {account.disabledByName}</Box>
        )}
      </>
    );
    return (
      <LightTooltip title={tooltip} placement="top">
        <Switch
          className="account-enabled-toggle"
          size="small"
          color="success"
          checked={isEnabled}
          onChange={() => openDisableDialog(account)}
          slotProps={{ input: { 'aria-label': isEnabled ? 'Disable account' : 'Enable account' } }}
        />
      </LightTooltip>
    );
  };

  const renderActions = (account: PPAccountIntegration) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {renderEnabledToggle(account)}
        {account.shopifyAppUpdateAvailable && onShopifyAppInstall && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShopifyUpdateAccount({ accountName: account.name })}
            sx={{ textTransform: 'none' }}
          >
            Update
          </Button>
        )}
        {onVerifyCheckoutChampCredentials && account.ppType === 'checkout_champ' && (
          <LightTooltip title="Verify credentials" placement="top-end">
            <IconButton
              onClick={() => setCredentialCheckAccount({ accountId: account.ppAccountId, accountName: account.name })}
              color="primary"
              size="small"
            >
              <VerifiedUserOutlinedIcon />
            </IconButton>
          </LightTooltip>
        )}
        {onChangeCheckoutChampPassword && account.ppType === 'checkout_champ' && (
          <LightTooltip title="Change password" placement="top-end">
            <IconButton
              onClick={() => setPasswordChangeAccount({ accountId: account.ppAccountId, accountName: account.name, loginId: account.loginId })}
              color="primary"
              size="small"
            >
              <LockResetIcon />
            </IconButton>
          </LightTooltip>
        )}
        {renderSyncAction(account)}
      </Box>
    );
  };

  const openCacheDialog = (account: PPAccountIntegration) => {
    setCacheEnabledInput(!!account.paymentCacheEnabled);
    setCacheTarget({
      ppType: account.ppType,
      accountId: account.ppAccountId,
      accountName: account.name,
      wasEnabled: !!account.paymentCacheEnabled,
    });
  };

  const renderCachePiiRows = (account: PPAccountIntegration) => {
    const accountKey = `${account.ppType}-${account.ppAccountId}`;
    const cacheEnabled = !!account.paymentCacheEnabled;
    const customerInfoOn = !!account.customerInfoEnabled;
    const missingReadCustomers = account.hasReadCustomersScope === false;
    const showCustomerInfo = account.ppType !== 'stripe';
    return (
      <>
        <InfoRow label="Payments cache">
          <Chip
            label={cacheEnabled ? 'On' : 'Off'}
            size="small"
            color={cacheEnabled ? 'success' : 'default'}
            variant="outlined"
            onClick={() => openCacheDialog(account)}
            sx={{ cursor: 'pointer', fontSize: '0.75rem', justifySelf: 'start' }}
          />
        </InfoRow>
        {showCustomerInfo && (
        <InfoRow label="Customer info">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={customerInfoOn ? 'On' : 'Off'}
              size="small"
              color={customerInfoOn ? 'success' : 'default'}
              variant="outlined"
              onClick={(e) => {
                setCustomerInfoMenuAnchor(e.currentTarget);
                setEditingCustomerInfoKey(accountKey);
              }}
              sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
            />
            {missingReadCustomers && (
              <LightTooltip
                title="Missing read_customers scope — customer info import cannot be enabled. Reinstall the Shopify app to grant this permission."
                placement="top"
              >
                <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'help' }} />
              </LightTooltip>
            )}
            <Menu
              anchorEl={editingCustomerInfoKey === accountKey ? customerInfoMenuAnchor : null}
              open={editingCustomerInfoKey === accountKey && !!customerInfoMenuAnchor}
              onClose={() => { setCustomerInfoMenuAnchor(null); setEditingCustomerInfoKey(null); }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {[false, true].map((enabled) => {
                const itemDisabled = enabled && missingReadCustomers;
                return (
                  <MenuItem
                    key={String(enabled)}
                    selected={enabled === customerInfoOn}
                    disabled={itemDisabled}
                    onClick={() => {
                      setCustomerInfoMenuAnchor(null);
                      setEditingCustomerInfoKey(null);
                      if (enabled !== customerInfoOn) {
                        setCustomerInfoConfirm({
                          ppType: account.ppType,
                          accountId: account.ppAccountId,
                          accountName: account.name,
                          enabled,
                          cacheOff: enabled && !cacheEnabled,
                        });
                      }
                    }}
                  >
                    {enabled ? 'On' : 'Off'}
                    {itemDisabled && ' — missing read_customers scope'}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        </InfoRow>
        )}
      </>
    );
  };

  const closePendingModeChange = () => {
    if (pendingModeSaving) return;
    setPendingModeError(null);
    setPendingModeChange(null);
  };

  const confirmPendingModeChange = async () => {
    setPendingModeError(null);
    setPendingModeSaving(true);
    try {
      await pendingModeChange?.onConfirm();
      setPendingModeChange(null);
    } catch (err: any) {
      setPendingModeError(err.message || 'Failed to save');
    } finally {
      setPendingModeSaving(false);
    }
  };

  const requestModeChange = (
    account: PPAccountIntegration,
    title: string,
    actionLabel: string,
    modeLabel: string,
    payload: Parameters<NonNullable<typeof onAccountSettingsSave>>[2],
    detail?: ReactNode,
  ) => {
    setPendingModeChange({
      title,
      description: (
        <>
          Change {actionLabel} for <strong>{account.name}</strong> to <strong>{modeLabel}</strong>?
          {detail}
        </>
      ),
      onConfirm: async () => {
        if (onAccountSettingsSave) {
          await onAccountSettingsSave(account.ppType, account.ppAccountId, payload);
        }
      },
    });
  };

  const renderAutoRefundRow = (
    account: PPAccountIntegration,
    { showScopeWarning }: { showScopeWarning: boolean },
  ) => {
    const missingWriteOrders = showScopeWarning && account.hasWriteOrdersScope === false;
    return (
      <ModeSelectRow
        label="Auto refund"
        mode={getAutoRefundMode(account)}
        modes={AUTO_REFUND_MODES}
        modeLabel={AutoRefundModeLabel}
        chipColor={chipColorForAutoRefund}
        adornment={missingWriteOrders ? (
          <LightTooltip
            title="Missing write_orders scope — auto-refund cannot issue refunds. Reinstall the Shopify app to grant this permission."
            placement="top"
          >
            <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'help' }} />
          </LightTooltip>
        ) : undefined}
        onPick={(m) => requestModeChange(account, 'Change Auto Refund Mode', 'auto refund mode', AutoRefundModeLabel[m], { autoRefundMode: m })}
      />
    );
  };

  const renderRefundMaxRow = (account: PPAccountIntegration) => (
    <InfoRow label="Max refund amount">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Chip
          label={account.refundMaxAmount != null
            ? formatCurrency(account.refundMaxAmount, 'USD', true, 0)
            : companyRefundMaxAmount != null
              ? `${formatCurrency(companyRefundMaxAmount, 'USD', true, 0)} (company default)`
              : 'Company default'}
          size="small"
          variant="outlined"
          color="success"
          onClick={() => setRefundMaxTarget({
            ppType: account.ppType,
            accountId: account.ppAccountId,
            accountName: account.name,
            refundMaxAmount: account.refundMaxAmount,
          })}
          sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
        />
      </Box>
    </InfoRow>
  );

  const renderRepresentmentStartRow = (account: PPAccountIntegration) => (
    <InfoRow label="Represented from">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Chip
          label={account.representmentStartDate ?? 'Not set'}
          size="small"
          variant="outlined"
          color="success"
          onClick={() => setRepresentmentTarget({
            ppType: account.ppType,
            accountId: account.ppAccountId,
            accountName: account.name,
            representmentStartDate: account.representmentStartDate,
          })}
          sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
        />
      </Box>
    </InfoRow>
  );

  const renderShopifyTaggingRow = (account: PPAccountIntegration) => {
    const mode: 'off' | 'on' = account.shopifyOrderTaggingEnabled ? 'on' : 'off';
    return (
      <ModeSelectRow<'off' | 'on'>
        label="Shopify tagging"
        mode={mode}
        modes={['off', 'on']}
        modeLabel={{ off: 'Off', on: 'On' }}
        chipColor={(m) => (m === 'on' ? 'success' : 'default')}
        onPick={(m) => requestModeChange(account, 'Change Shopify Order Tagging', 'Shopify order tagging', m === 'on' ? 'On' : 'Off', { shopifyOrderTaggingEnabled: m === 'on' })}
      />
    );
  };

  const renderShopifyOrderCancelRow = (account: PPAccountIntegration) => {
    const mode: 'off' | 'on' = account.shopifyOrderCancelEnabled ? 'on' : 'off';
    return (
      <ModeSelectRow<'off' | 'on'>
        label="Shopify order cancel"
        mode={mode}
        modes={['off', 'on']}
        modeLabel={{ off: 'Off', on: 'On' }}
        chipColor={(m) => (m === 'on' ? 'success' : 'default')}
        onPick={(m) => requestModeChange(account, 'Change Shopify Order Cancel', 'Shopify order cancel', m === 'on' ? 'On' : 'Off', { shopifyOrderCancelEnabled: m === 'on' })}
      />
    );
  };

  const renderVampRatesRow = (account: PPAccountIntegration) => {
    const mode: 'off' | 'on' = account.vampRatesEnabled ? 'on' : 'off';
    return (
      <ModeSelectRow<'off' | 'on'>
        label="VAMP rates"
        mode={mode}
        modes={['off', 'on']}
        modeLabel={{ off: 'Off', on: 'On' }}
        chipColor={(m) => (m === 'on' ? 'success' : 'default')}
        onPick={(m) => requestModeChange(account, 'Change VAMP Rates', 'VAMP rates', m === 'on' ? 'On' : 'Off', { vampRatesEnabled: m === 'on' })}
      />
    );
  };

  const renderSubscriptionCancelRow = (account: PPAccountIntegration) => (
    <ModeSelectRow
      label="Cancel subscription"
      mode={getSubscriptionCancelMode(account)}
      modes={CANCELLATION_MODES}
      modeLabel={CancellationModeLabel}
      chipColor={chipColorForCancellation}
      onPick={(m) => requestModeChange(account, 'Change Cancel Subscription Mode', 'cancel subscription mode', CancellationModeLabel[m], { subscriptionCancelMode: m })}
    />
  );

  const renderExternalRefundRow = (account: PPAccountIntegration) => (
    <ModeSelectRow
      label="External refund"
      mode={getExternalRefundMode(account)}
      modes={EXTERNAL_REFUND_MODES}
      modeLabel={ExternalRefundModeLabel}
      chipColor={chipColorForExternalRefund}
      onPick={(m) => requestModeChange(account, 'Change External Refund Mode', 'external refund mode', ExternalRefundModeLabel[m], { externalRefundMode: m })}
    />
  );

  const renderMarkChargebackRow = (account: PPAccountIntegration) => (
    <ModeSelectRow
      label="Mark chargeback"
      mode={getMarkChargebackMode(account)}
      modes={MARK_CHARGEBACK_MODES}
      modeLabel={MarkChargebackModeLabel}
      chipColor={chipColorForMarkChargeback}
      onPick={(m) => requestModeChange(account, 'Change Mark Chargeback Mode', 'mark chargeback mode', MarkChargebackModeLabel[m], { markChargebackMode: m })}
    />
  );

  const renderFlagRow = (
    account: PPAccountIntegration,
    label: string,
    field: 'orderCancelEnabled',
  ) => (
    <ModeSelectRow<'off' | 'on'>
      label={label}
      mode={account[field] ? 'on' : 'off'}
      modes={['off', 'on']}
      modeLabel={{ off: 'Off', on: 'On' }}
      chipColor={(m) => (m === 'on' ? 'success' : 'default')}
      onPick={(m) => requestModeChange(account, `Change ${label}`, label.toLowerCase(), m === 'on' ? 'On' : 'Off', { [field]: m === 'on' })}
    />
  );

  const renderProxyRow = (account: PPAccountIntegration) => (
    <ModeSelectRow<'off' | 'on'>
      label="Proxy"
      mode={account.useProxy ? 'on' : 'off'}
      modes={['off', 'on']}
      modeLabel={{ off: 'Off', on: 'On' }}
      chipColor={(m) => (m === 'on' ? 'success' : 'default')}
      onPick={(m) => requestModeChange(
        account,
        'Change Proxy',
        'proxy routing',
        m === 'on' ? 'On' : 'Off',
        { useProxy: m === 'on' },
        <Box component="div" sx={{ display: 'block', mt: 1.5 }}>
          The client's CRM must allow <strong>{CRM_CONNECTOR_PROXY_IP}</strong>. Saving re-checks the API key through the new route.
        </Box>,
      )}
    />
  );

  const renderSubscriptionAppCancelRow = (
    account: PPAccountIntegration,
    opts: {
      label: string;
      mode: CancellationMode;
      title: string;
      actionLabel: string;
      payload: (m: CancellationMode) => Parameters<NonNullable<typeof onAccountSettingsSave>>[2];
      hasToken: boolean;
      tokenReason: string;
    },
  ) => (
    <ModeSelectRow
      label={opts.label}
      mode={opts.mode}
      modes={CANCELLATION_MODES}
      modeLabel={CancellationModeLabel}
      chipColor={chipColorForCancellation}
      disabledItem={(m) => ({ disabled: m !== 'off' && !opts.hasToken, reason: opts.tokenReason })}
      onPick={(m) => requestModeChange(account, opts.title, opts.actionLabel, CancellationModeLabel[m], opts.payload(m))}
    />
  );

  if (accounts.length === 0 && !loading) {
    return (
      <Box className="text-center py-8">
        <Typography variant="body1" color="text.secondary">
          No accounts found.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {loading && !downloadingAccountId && (
        <Box className="flex justify-center items-center py-8">
          <Typography>Loading Payment Processors...</Typography>
        </Box>
      )}

      {loading && downloadingAccountId && (
        <Box className="flex justify-center items-center py-8">
          <Typography>Downloading disputes...</Typography>
        </Box>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => {
                const accountKey = `${account.ppType}-${account.ppAccountId}`;
                const isShopify = account.ppType === 'shopify';
                const isCheckoutChamp = account.ppType === 'checkout_champ';
                const isCrmConnector = account.ppType === 'crm_connector';
                const crmDriverOption = CRM_CONNECTOR_DRIVERS.find(option => option.value === account.driver) ?? null;
                const isAdyen = account.ppType === 'adyen';
                const unmappedMerchants = account.unmappedMerchantCount ?? 0;
                const isDisabled = account.enabled === false;
                const loopMode = isShopify ? getLoopCancellationMode(account) : null;
                const chargebackLoopMode = isShopify ? getLoopCancellationOnChargebackMode(account) : null;
                const kachingMode = isShopify ? getKachingCancellationMode(account) : null;
                const chargebackKachingMode = isShopify ? getKachingCancellationOnChargebackMode(account) : null;

                return (
                  <TableRow
                    key={accountKey}
                    hover
                    sx={isDisabled ? { '& > td:not(:has(.account-enabled-toggle))': { opacity: 0.55 } } : undefined}
                  >
                    <TableCell sx={{ maxWidth: 200 }}>
                      <PpLogo ppType={account.ppType} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {account.shopUrl ? (
                          <a
                            href={account.shopUrl.startsWith('https://') ? account.shopUrl : `https://${account.shopUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'inherit', textDecoration: 'none' }}
                          >
                            {account.name}
                            <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </a>
                        ) : (
                          account.name
                        )}
                        {account.ppType === 'shopify' && (
                          <Chip
                            label={account.isPublicApp ? 'Public App' : 'Custom App'}
                            size="small"
                            color={account.isPublicApp ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: 180 }}>
                      {isAdyen ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
                          <Typography variant="body2">
                            {account.brands?.length
                              ? account.brands.map((brand) => brand.brandName).join(', ')
                              : 'Not mapped'}
                          </Typography>
                          {onAdyenMapMerchants && (
                            <Link
                              component="button"
                              type="button"
                              variant="caption"
                              underline="hover"
                              onClick={() => onAdyenMapMerchants(account.ppAccountId, account.name)}
                              sx={{ color: unmappedMerchants > 0 ? 'warning.main' : 'primary.main', textAlign: 'left' }}
                            >
                              {unmappedMerchants > 0
                                ? `⚠ ${unmappedMerchants} unmapped merchant${unmappedMerchants === 1 ? '' : 's'}`
                                : 'Map merchants'}
                            </Link>
                          )}
                        </Box>
                      ) : isCheckoutChamp || isCrmConnector ? (
                        <Typography variant="body2">{account.brandName ?? ''}</Typography>
                      ) : (
                        <>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: onBrandChange ? 'pointer' : 'default',
                          '& .brand-edit-icon': { opacity: 0 },
                          '&:hover .brand-edit-icon': onBrandChange ? { opacity: 1 } : {},
                        }}
                        onClick={(e) => {
                          if (!onBrandChange) return;
                          setBrandMenuAnchor(e.currentTarget);
                          setEditingBrandKey(accountKey);
                        }}
                      >
                        <Typography variant="body2">{account.brandName}</Typography>
                        {onBrandChange && (
                          <EditIcon className="brand-edit-icon" sx={{ fontSize: 14, color: 'text.secondary' }} />
                        )}
                      </Box>
                      <Menu
                        anchorEl={editingBrandKey === accountKey ? brandMenuAnchor : null}
                        open={editingBrandKey === accountKey && !!brandMenuAnchor}
                        onClose={() => { setBrandMenuAnchor(null); setEditingBrandKey(null); }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      >
                        {brands.map((brand) => (
                          <MenuItem
                            key={brand.brandId}
                            selected={brand.brandId === account.brandId}
                            onClick={async () => {
                              if (brand.brandId !== account.brandId && onBrandChange) {
                                await onBrandChange(account.ppType, account.ppAccountId, brand.brandId);
                              }
                              setBrandMenuAnchor(null);
                              setEditingBrandKey(null);
                            }}
                          >
                            {brand.brandName}
                          </MenuItem>
                        ))}
                      </Menu>
                        </>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 480 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                          columnGap: 3,
                          rowGap: 0.75,
                        }}
                      >
                        {account.ppType === 'stripe' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <InfoRow label="API key">
                              <EditableMonoValue
                                value={account.secret}
                                onEdit={canEditAccountSettings ? () => setApiKeyAccount(account) : undefined}
                              />
                            </InfoRow>
                            {account.webhookSecret !== undefined && (
                              <InfoRow label="Webhook secret"><MonoValue value={account.webhookSecret} /></InfoRow>
                            )}
                            {account.canSubmitEvidence != null && (
                              <InfoRow label="Disputes API">
                                <DisputesApiChip
                                  on={account.canSubmitEvidence}
                                  offTitle="This key lacks Disputes write or Files write; edit the restricted key in Stripe → Developers → API keys and re-open a dispute's evidence page."
                                />
                              </InfoRow>
                            )}
                            {onAccountSettingsSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderCachePiiRows(account)}
                              </HasPermission>
                            )}
                            {onRefundMaxSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderRefundMaxRow(account)}
                              </HasPermission>
                            )}
                            {onRepresentmentStartDateSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderRepresentmentStartRow(account)}
                              </HasPermission>
                            )}
                          </Box>
                        )}
                        {account.ppType === 'paypal' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <InfoRow label="Client secret"><MonoValue value={account.secret} /></InfoRow>
                            {onRefundMaxSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderRefundMaxRow(account)}
                              </HasPermission>
                            )}
                            {onRepresentmentStartDateSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderRepresentmentStartRow(account)}
                              </HasPermission>
                            )}
                          </Box>
                        )}
                        {account.ppType === 'adyen' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <InfoRow label="Environment"><MonoValue value={account.environment} /></InfoRow>
                            <InfoRow label="Adyen company"><MonoValue value={account.adyenCompanyName} /></InfoRow>
                            <InfoRow label="Disputes API">
                              <DisputesApiChip
                                on={Boolean(account.canSubmitEvidence)}
                                offTitle="The API credential lacks the API dispute management role, so defences go through Customer Area. Add the role to the credential and open a case file; the portal re-checks then."
                              />
                            </InfoRow>
                            {onRefundMaxSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderRefundMaxRow(account)}
                              </HasPermission>
                            )}
                            {onRepresentmentStartDateSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                {renderRepresentmentStartRow(account)}
                              </HasPermission>
                            )}
                            {onAdyenImportDisputes && (
                              <InfoRow label="Dispute report">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Chip
                                    label="Import disputes"
                                    icon={<UploadFileIcon />}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setAdyenImportTarget({
                                      accountId: account.ppAccountId,
                                      accountName: account.name,
                                    })}
                                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                  />
                                </Box>
                              </InfoRow>
                            )}
                            {onAdyenImportPayments && (
                              <InfoRow label="Transaction report">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Chip
                                    label="Import transactions"
                                    icon={<UploadFileIcon />}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setAdyenPaymentImportTarget({
                                      accountId: account.ppAccountId,
                                      accountName: account.name,
                                    })}
                                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                  />
                                  {onAdyenPaymentDays && (
                                    <Chip
                                      label="Imported dates"
                                      icon={<CalendarMonthIcon />}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => setAdyenPaymentDaysTarget({
                                        accountId: account.ppAccountId,
                                        accountName: account.name,
                                      })}
                                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                    />
                                  )}
                                </Box>
                              </InfoRow>
                            )}
                          </Box>
                        )}
                        {isCheckoutChamp && (
                          <>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <InfoRow label="Login ID"><MonoValue value={account.loginId} /></InfoRow>
                              {onAccountSettingsSave && (
                                <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                  {renderCachePiiRows(account)}
                                  {renderAutoRefundRow(account, { showScopeWarning: false })}
                                  {onRefundMaxSave && renderRefundMaxRow(account)}
                                </HasPermission>
                              )}
                            </Box>
                            {onAccountSettingsSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {renderSubscriptionCancelRow(account)}
                                  {renderShopifyTaggingRow(account)}
                                  {renderShopifyOrderCancelRow(account)}
                                  {renderExternalRefundRow(account)}
                                  {renderMarkChargebackRow(account)}
                                </Box>
                              </HasPermission>
                            )}
                          </>
                        )}
                        {isCrmConnector && (
                          <>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {account.driver && (
                                <InfoRow label="Driver"><MonoValue value={crmDriverOption?.label ?? account.driver} /></InfoRow>
                              )}
                              <InfoRow label="Base URL"><MonoValue value={account.baseUrl} /></InfoRow>
                              <InfoRow label="API key">
                                <EditableMonoValue
                                  value={account.displayApiKey}
                                  onEdit={canEditAccountSettings ? () => setConnectorKeyAccount(account) : undefined}
                                />
                              </InfoRow>
                              {(crmDriverOption === null || crmDriverOption.hasSandbox) && (
                                <InfoRow label="Sandbox">
                                  <EditableMonoValue
                                    value={account.sandboxBaseUrl ?? 'Not set'}
                                    onEdit={canEditAccountSettings ? () => setSandboxAccount(account) : undefined}
                                  />
                                </InfoRow>
                              )}
                              {onAccountSettingsSave && (
                                <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                  {renderProxyRow(account)}
                                  {renderCachePiiRows(account)}
                                  <InfoRow label="Cache window">
                                    <Chip
                                      label={`${account.paymentCacheDays ?? CRM_MAX_CACHE_WINDOW_DAYS} days`}
                                      size="small"
                                      variant="outlined"
                                      onClick={() => setCacheWindowAccount(account)}
                                      sx={{ cursor: 'pointer', fontSize: '0.75rem', justifySelf: 'start' }}
                                    />
                                  </InfoRow>
                                  {renderAutoRefundRow(account, { showScopeWarning: false })}
                                  {onRefundMaxSave && renderRefundMaxRow(account)}
                                </HasPermission>
                              )}
                            </Box>
                            {onAccountSettingsSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {renderSubscriptionCancelRow(account)}
                                  {renderExternalRefundRow(account)}
                                  {renderFlagRow(account, 'Order cancel', 'orderCancelEnabled')}
                                  {account.recordChargebackSupported && renderMarkChargebackRow(account)}
                                </Box>
                              </HasPermission>
                            )}
                            {onAccountSettingsSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                  <InfoRow label="Supported actions">
                                    <EditableValue onEdit={() => setActionsAccount(account)}>
                                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {CRM_CONNECTOR_CAPABILITIES.map(({ field, title }) => (
                                          <Chip
                                            key={field}
                                            label={title}
                                            size="small"
                                            variant="outlined"
                                            color={account[field] ? 'success' : 'default'}
                                            sx={{ fontSize: '0.75rem' }}
                                          />
                                        ))}
                                      </Box>
                                    </EditableValue>
                                  </InfoRow>
                                </Box>
                              </HasPermission>
                            )}
                          </>
                        )}
                        {isShopify && (
                          <>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <InfoRow label="App version">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <MonoValue value={formatVersion(account.shopifyAppVersion)} />
                                  <Chip
                                    label={account.shopifyAppUpdateAvailable ? 'Update available' : 'Latest'}
                                    size="small"
                                    color={account.shopifyAppUpdateAvailable ? 'warning' : 'success'}
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </InfoRow>
                              <InfoRow label="Access token"><MonoValue value={account.secret} /></InfoRow>
                              {onAccountSettingsSave && (
                                <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                  {renderCachePiiRows(account)}
                                  {renderAutoRefundRow(account, { showScopeWarning: true })}
                                  {onRefundMaxSave && renderRefundMaxRow(account)}
                                  {onRepresentmentStartDateSave && renderRepresentmentStartRow(account)}
                                </HasPermission>
                              )}
                            </Box>
                            {onAccountSettingsSave && (
                              <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  <InfoRow label="Loop token">
                                    <EditableMonoValue
                                      value={account.displayLoopToken ?? '— Not set'}
                                      onEdit={canWriteCompanies ? () => setTokenDialog({ account, app: 'loop' }) : undefined}
                                    />
                                  </InfoRow>
                                  {renderSubscriptionAppCancelRow(account, {
                                    label: 'Loop cancel alerts',
                                    mode: loopMode!,
                                    title: 'Change Loop Cancel Alerts Mode',
                                    actionLabel: 'Loop cancel alerts mode',
                                    payload: (m) => ({ loopCancellationMode: m }),
                                    hasToken: !!account.displayLoopToken,
                                    tokenReason: 'set Loop token first',
                                  })}
                                  {renderSubscriptionAppCancelRow(account, {
                                    label: 'Loop cancel on chargeback',
                                    mode: chargebackLoopMode!,
                                    title: 'Change Loop Cancel on Chargeback Mode',
                                    actionLabel: 'Loop cancel on chargeback mode',
                                    payload: (m) => ({ loopCancellationOnChargebackMode: m }),
                                    hasToken: !!account.displayLoopToken,
                                    tokenReason: 'set Loop token first',
                                  })}
                                  <InfoRow label="Kaching token">
                                    <EditableMonoValue
                                      value={account.displayKachingToken ?? '— Not set'}
                                      onEdit={canWriteCompanies ? () => setTokenDialog({ account, app: 'kaching' }) : undefined}
                                    />
                                  </InfoRow>
                                  {renderSubscriptionAppCancelRow(account, {
                                    label: 'Kaching cancel alerts',
                                    mode: kachingMode!,
                                    title: 'Change Kaching Cancel Alerts Mode',
                                    actionLabel: 'Kaching cancel alerts mode',
                                    payload: (m) => ({ kachingCancellationMode: m }),
                                    hasToken: !!account.displayKachingToken,
                                    tokenReason: 'set Kaching token first',
                                  })}
                                  {renderSubscriptionAppCancelRow(account, {
                                    label: 'Kaching cancel on chargeback',
                                    mode: chargebackKachingMode!,
                                    title: 'Change Kaching Cancel on Chargeback Mode',
                                    actionLabel: 'Kaching cancel on chargeback mode',
                                    payload: (m) => ({ kachingCancellationOnChargebackMode: m }),
                                    hasToken: !!account.displayKachingToken,
                                    tokenReason: 'set Kaching token first',
                                  })}
                                </Box>
                              </HasPermission>
                            )}
                          </>
                        )}
                        {VAMP_RATED_PP_TYPES.includes(account.ppType) && onAccountSettingsSave && (
                          <HasPermission permission={PERMISSIONS.COMPANIES_WRITE_ALL}>
                            {/* Spans the grid so the row sits in the same place for every provider; left to
                                auto-flow it lands in whichever column the provider's group count leaves free. */}
                            <Box sx={{ gridColumn: '1 / -1' }}>
                              {renderVampRatesRow(account)}
                            </Box>
                          </HasPermission>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderActions(account)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!pendingModeChange} onClose={closePendingModeChange}>
        <DialogTitle>{pendingModeChange?.title}</DialogTitle>
        <DialogContent>
          {pendingModeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pendingModeError}
            </Alert>
          )}
          <DialogContentText component="div">{pendingModeChange?.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePendingModeChange} disabled={pendingModeSaving}>Cancel</Button>
          <Button variant="contained" onClick={confirmPendingModeChange} disabled={pendingModeSaving}>
            {pendingModeSaving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <ShopifyAppUpdateDialog
        open={!!shopifyUpdateAccount}
        accountNames={shopifyUpdateAccount ? [shopifyUpdateAccount.accountName] : []}
        onClose={() => setShopifyUpdateAccount(null)}
        onUpdate={() => onShopifyAppInstall?.()}
      />

      <SubscriptionAppTokenDialog
        open={!!tokenDialog}
        account={tokenDialog?.account ?? null}
        app={tokenDialog?.app ?? 'loop'}
        onClose={() => setTokenDialog(null)}
        onSave={async (token, enableCancellation) => {
          if (tokenDialog && onAccountSettingsSave) {
            const { account, app } = tokenDialog;
            await onAccountSettingsSave(
              account.ppType,
              account.ppAccountId,
              app === 'loop'
                ? {
                  loopSubscriptionsToken: token,
                  ...(enableCancellation && { loopCancellationMode: 'cancel', loopCancellationOnChargebackMode: 'cancel' }),
                }
                : {
                  kachingToken: token,
                  ...(enableCancellation && { kachingCancellationMode: 'cancel', kachingCancellationOnChargebackMode: 'cancel' }),
                },
            );
          }
        }}
      />

      <CredentialDialog
        open={!!apiKeyAccount}
        credentialLabel="API key"
        helperText={STRIPE_API_KEY_HELP}
        accountName={apiKeyAccount?.name ?? ''}
        currentValue={apiKeyAccount?.secret}
        onClose={() => setApiKeyAccount(null)}
        onSave={async (secret) => {
          if (apiKeyAccount && onAccountSettingsSave) {
            await onAccountSettingsSave(apiKeyAccount.ppType, apiKeyAccount.ppAccountId, { secret });
          }
        }}
      />

      <CredentialDialog
        open={!!connectorKeyAccount}
        credentialLabel="API key"
        helperText={CRM_CONNECTOR_API_KEY_HELP}
        accountName={connectorKeyAccount?.name ?? ''}
        currentValue={connectorKeyAccount?.displayApiKey}
        onClose={() => setConnectorKeyAccount(null)}
        onSave={async (value) => {
          if (connectorKeyAccount && onAccountSettingsSave) {
            await onAccountSettingsSave(connectorKeyAccount.ppType, connectorKeyAccount.ppAccountId, { apiKey: value });
          }
        }}
      />

      {actionsAccount && (
        <CrmConnectorActionsDialog
          open
          accountName={actionsAccount.name}
          current={{
            markRefundedSupported: Boolean(actionsAccount.markRefundedSupported),
            orderTagsSupported: Boolean(actionsAccount.orderTagsSupported),
            recordChargebackSupported: Boolean(actionsAccount.recordChargebackSupported),
          }}
          onClose={() => setActionsAccount(null)}
          onSave={async (values) => {
            if (onAccountSettingsSave) {
              await onAccountSettingsSave(actionsAccount.ppType, actionsAccount.ppAccountId, values);
            }
          }}
        />
      )}

      <CrmConnectorSandboxDialog
        open={!!sandboxAccount}
        accountName={sandboxAccount?.name ?? ''}
        currentBaseUrl={sandboxAccount?.sandboxBaseUrl}
        currentDisplayKey={sandboxAccount?.displaySandboxApiKey}
        isNativeContract={!sandboxAccount?.driver}
        onClose={() => setSandboxAccount(null)}
        onSave={async (sandboxBaseUrl, sandboxApiKey) => {
          if (sandboxAccount && onAccountSettingsSave) {
            await onAccountSettingsSave(sandboxAccount.ppType, sandboxAccount.ppAccountId, { sandboxBaseUrl, sandboxApiKey });
          }
        }}
      />

      <CrmConnectorCacheWindowDialog
        open={!!cacheWindowAccount}
        accountName={cacheWindowAccount?.name ?? ''}
        currentDays={cacheWindowAccount?.paymentCacheDays}
        onClose={() => setCacheWindowAccount(null)}
        onSave={async (paymentCacheDays) => {
          if (cacheWindowAccount && onAccountSettingsSave) {
            await onAccountSettingsSave(cacheWindowAccount.ppType, cacheWindowAccount.ppAccountId, { paymentCacheDays });
          }
        }}
      />

      <Dialog open={!!cacheTarget} onClose={() => setCacheTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Payments Cache</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            Payments cache settings for <strong>{cacheTarget?.accountName}</strong>.
          </DialogContentText>
          <FormControlLabel
            control={(
              <Switch
                checked={cacheEnabledInput}
                onChange={(e) => setCacheEnabledInput(e.target.checked)}
              />
            )}
            label={cacheEnabledInput ? 'On' : 'Off'}
          />
          {cacheEnabledInput && !cacheTarget?.wasEnabled && (
            <Box sx={{ mt: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>
              {cacheTarget?.ppType === 'stripe'
                ? 'Saving starts importing the last 122 days.'
                : 'Saving starts importing the last year of payments. Days already cached are skipped.'}
            </Box>
          )}
          {!cacheEnabledInput && (
            <Box sx={{ mt: 1.5, color: 'warning.main', fontSize: '0.8rem' }}>
              Order Insights and other features that depend on the cache will stop
              processing alerts that need cached data while this is off.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCacheTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (cacheTarget && onAccountSettingsSave) {
                await onAccountSettingsSave(cacheTarget.ppType, cacheTarget.accountId, { paymentCacheEnabled: cacheEnabledInput });
              }
              setCacheTarget(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!customerInfoConfirm} onClose={() => setCustomerInfoConfirm(null)}>
        <DialogTitle>{customerInfoConfirm?.enabled ? 'Enable Customer Info Import' : 'Disable Customer Info Import'}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {customerInfoConfirm?.enabled ? 'Enable' : 'Disable'} customer info import for{' '}
            <strong>{customerInfoConfirm?.accountName}</strong>?
            {customerInfoConfirm?.cacheOff && (
              <Box sx={{ mt: 1.5, color: 'warning.main' }}>
                Payments cache is currently off. Historical customer info won't be backfilled until the cache
                is re-enabled. New records will still populate as orders arrive.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerInfoConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (customerInfoConfirm && onAccountSettingsSave) {
                await onAccountSettingsSave(customerInfoConfirm.ppType, customerInfoConfirm.accountId, { customerInfoEnabled: customerInfoConfirm.enabled });
              }
              setCustomerInfoConfirm(null);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!disableTarget} onClose={() => setDisableTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{disableTarget?.isCurrentlyDisabled ? 'Enable account' : 'Disable account'}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {disableTarget?.isCurrentlyDisabled ? (
              <>Re-enable <strong>{disableTarget?.accountName}</strong>? All automations resume with its saved configuration intact. Every Ethoca, CDRN and RDR merchant mapping that names this account is re-activated, including any you deactivated by hand.</>
            ) : (
              <>Disable <strong>{disableTarget?.accountName}</strong>? All automations for this account will stop, and every Ethoca, CDRN and RDR merchant mapping that names this account is deactivated. Nothing is deleted — its configuration and mappings are kept, so re-enabling restores them.</>
            )}
          </DialogContentText>
          {!disableTarget?.isCurrentlyDisabled && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Reason (optional)"
              placeholder="e.g. provider closed the account"
              value={disableReasonInput}
              onChange={(e) => setDisableReasonInput(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={disableTarget?.isCurrentlyDisabled ? 'primary' : 'error'}
            onClick={async () => {
              if (disableTarget && onAccountSettingsSave) {
                await onAccountSettingsSave(
                  disableTarget.ppType,
                  disableTarget.accountId,
                  disableTarget.isCurrentlyDisabled
                    ? { enabled: true }
                    : { enabled: false, disabledReason: disableReasonInput.trim() || null },
                );
              }
              setDisableTarget(null);
            }}
          >
            {disableTarget?.isCurrentlyDisabled ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>

      {adyenImportTarget && onAdyenImportDisputes && (
        <AdyenDisputeImportDialog
          accountId={adyenImportTarget.accountId}
          accountName={adyenImportTarget.accountName}
          onImport={onAdyenImportDisputes}
          onClose={() => setAdyenImportTarget(null)}
        />
      )}

      {adyenPaymentImportTarget && onAdyenImportPayments && (
        <AdyenPaymentImportDialog
          accountId={adyenPaymentImportTarget.accountId}
          accountName={adyenPaymentImportTarget.accountName}
          onImport={onAdyenImportPayments}
          onClose={() => setAdyenPaymentImportTarget(null)}
        />
      )}

      {adyenPaymentDaysTarget && onAdyenPaymentDays && (
        <AdyenPaymentDaysDialog
          accountId={adyenPaymentDaysTarget.accountId}
          accountName={adyenPaymentDaysTarget.accountName}
          onLoad={onAdyenPaymentDays}
          onClose={() => setAdyenPaymentDaysTarget(null)}
        />
      )}

      <RefundMaxAmountDialog
        open={!!refundMaxTarget}
        description={<>Alerts for <strong>{refundMaxTarget?.accountName}</strong> above this amount are not refunded — they are resolved as declined instead.</>}
        initialCents={refundMaxTarget?.refundMaxAmount}
        onClose={() => setRefundMaxTarget(null)}
        onSave={async (cents) => {
          if (refundMaxTarget && onRefundMaxSave) {
            await onRefundMaxSave(refundMaxTarget.ppType, refundMaxTarget.accountId, cents);
          }
        }}
        onReset={refundMaxTarget?.refundMaxAmount != null ? async () => {
          if (refundMaxTarget && onRefundMaxSave) {
            await onRefundMaxSave(refundMaxTarget.ppType, refundMaxTarget.accountId, null);
          }
        } : undefined}
        resetLabel="Use company default"
      />

      <RepresentmentStartDateDialog
        open={!!representmentTarget}
        accountName={representmentTarget?.accountName ?? ''}
        initialDate={representmentTarget?.representmentStartDate}
        onClose={() => setRepresentmentTarget(null)}
        onSave={async (date) => {
          if (representmentTarget && onRepresentmentStartDateSave) {
            await onRepresentmentStartDateSave(representmentTarget.ppType, representmentTarget.accountId, date);
          }
        }}
      />

      {credentialCheckAccount && onVerifyCheckoutChampCredentials && (
        <CheckoutChampCredentialCheckDialog
          open
          accountName={credentialCheckAccount.accountName}
          onClose={() => setCredentialCheckAccount(null)}
          onCheck={() => onVerifyCheckoutChampCredentials(credentialCheckAccount.accountId)}
        />
      )}

      {passwordChangeAccount && onChangeCheckoutChampPassword && (
        <CheckoutChampPasswordDialog
          open
          accountName={passwordChangeAccount.accountName}
          loginId={passwordChangeAccount.loginId}
          onClose={() => setPasswordChangeAccount(null)}
          onSave={(password) => onChangeCheckoutChampPassword(passwordChangeAccount.accountId, password)}
        />
      )}
    </>
  );
}
